import { useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { Link } from 'react-router';

import Icon from '../../components/Icon';
import AboutModal from '../about/AboutModal';
import { TOP_STRIP_H } from '../../components/Frame';
import { FONTS } from '../../shared/theme/typography';
import { usePhoneChrome } from '../../shared/usePhoneChrome';
import { CG_RATIO, FEEDBACK_URL, LOCKUP_GAP, STRIP_X, WORDMARK_W } from '../../shared/utils/constants';

/**
 * The header across the top of the pages drawn on the background (D3, Sept 19).
 *
 * The same lockup and links as the app's own top strip, with one difference
 * that matters: everything here takes `currentColor`, which `BackdropPage`
 * drives from off-black to parchment as the picture behind it turns to flat
 * green. Reusing `components/Lockup` would not do — it paints itself parchment,
 * which is invisible against this page's near-white sky.
 */

const Bar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  height: TOP_STRIP_H,
  padding: `0 ${STRIP_X}px`,
  [theme.breakpoints.down('md')]: { height: 'auto', padding: '11px 16px', gap: 10 },
  [theme.breakpoints.down('sm')]: { padding: '10px 12px', gap: 14 },
}));

const Mark = styled('div')({
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
});

const Wordmark = styled(Link)(({ theme }) => ({
  display: 'block',
  width: WORDMARK_W.wide,
  color: 'inherit',
  flexShrink: 0,
  transition: 'opacity 120ms ease',
  '&:hover': { opacity: 0.75 },
  [theme.breakpoints.down('md')]: { width: WORDMARK_W.narrow },
  [theme.breakpoints.down('sm')]: { width: WORDMARK_W.phone },
}));

const By = styled('span')(({ theme }) => ({
  margin: `0 ${LOCKUP_GAP / 2}px`,
  fontFamily: FONTS.body,
  fontSize: 15,
  fontWeight: 700,
  opacity: 0.6,
  [theme.breakpoints.down('sm')]: { margin: '0 6px', fontSize: 11 },
}));

const CgMark = styled('a')(({ theme }) => ({
  display: 'block',
  width: WORDMARK_W.wide * CG_RATIO,
  color: 'inherit',
  flexShrink: 0,
  transition: 'opacity 120ms ease',
  '&:hover': { opacity: 0.75 },
  [theme.breakpoints.down('md')]: { width: 30 },
}));

const Links = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 24,
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('md')]: { gap: 16 },
}));

const HeaderLink = styled('button')({
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: FONTS.display,
  fontSize: 14,
  fontWeight: 700,
  color: 'inherit',
  '&:hover': { opacity: 0.75 },
});

export default function BackdropHeader() {
  const [about, setAbout] = useState(false);
  const theme = useTheme();
  /** Below 900px: the lockup shrinks and About Us gives up its place (App's rule). */
  const narrow = useMediaQuery(theme.breakpoints.down('md'));
  /**
   * Below 900px a window that is not a phone keeps the view toggle in its own
   * header, drawn over this one — so this one gives up its remaining link
   * rather than run under it. A phone's toggle is at the foot of the screen,
   * and its header keeps "Submit Feedback!" as the mockups draw it.
   */
  const phoneChrome = usePhoneChrome();
  const toggleOverhead = narrow && !phoneChrome;
  return (
    <>
      <Bar>
        <Mark>
          <Wordmark to="/" aria-label="My Climate Week — back to all events">
            <Icon name="logo:mcw" size="100%" title="My Climate Week" />
          </Wordmark>
          <By aria-hidden="true">by</By>
          <CgMark
            href="https://www.commonground.boston/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="CommonGround (opens in a new tab)"
          >
            {/* The wide lockup's wordmark, and the phone's compact tree. */}
            <Icon name={narrow ? 'logo:cgMark' : 'logo:cg'} size="100%" title="CommonGround" />
          </CgMark>
        </Mark>

        <Links>
          {/* No room for a third item in a phone's header; the mockups drop it. */}
          {!narrow && <HeaderLink onClick={() => setAbout(true)}>About Us</HeaderLink>}
          {!toggleOverhead && (
            <HeaderLink onClick={() => window.open(FEEDBACK_URL, '_blank', 'noopener,noreferrer')}>
              Submit Feedback!
            </HeaderLink>
          )}
        </Links>
      </Bar>

      <AboutModal open={about} onClose={() => setAbout(false)} />
    </>
  );
}
